// src/directives/inWindow.js
/* eslint valid-jsdoc: "error" */

import { isNowInWindow } from "@/utils/dateWindow";

/*
 * @file Директива v-in-window та зручні хелпери для керування відображенням елементів у визначені «вікна дат».
 * Підтримує формати: 'DD.MM', 'YYYY-MM-DD', діапазони, кілька вікон, інверсію, режими приховування та кастомні класи.
 */

/*
 * @typedef {Object} InWindowSingle
 * @property {string|Date} start - Початок вікна: 'DD.MM' або 'YYYY-MM-DD' або Date
 * @property {string|Date} end   - Кінець вікна: 'DD.MM' або 'YYYY-MM-DD' або Date
 */

/*
 * @typedef {Object} InWindowOptions
 * @property {string=} tz - IANA таймзона (за замовчуванням 'Europe/Kyiv')
 * @property {boolean=} invert - Якщо true — елемент ХОВАЄТЬСЯ під час вікна, інакше ПОКАЗУЄТЬСЯ
 * @property {"display"|"visibility"|"classOnly"=} mode - Як перемикати: display (default) / visibility / лише класами
 * @property {string=} onClass  - Додається, коли елемент активний (видимий)
 * @property {string=} offClass - Додається, коли елемент неактивний (прихований)
 * @property {number=} refreshMs - Період повторної перевірки в мс; якщо не задано — наступна північ у TZ
 */

/*
 * Створює Date «зараз» у заданій таймзоні.
 * @param {string=} tz - IANA таймзона
 * @returns {Date} Поточний час у вибраній TZ
 */
function nowInTZ(tz) {
	const zone = tz || "Europe/Kyiv";
	// Локалізуємо в потрібну TZ і назад у Date
	return new Date(new Date().toLocaleString("en-US", { timeZone: zone }));
}

/*
 * Розбір значення директиви у список вікон + опції.
 * Підтримувані значення:
 * - Масив:           v-in-window="['25.10','01.11']"
 * - Рядок:           v-in-window="'25.10-01.11'"
 * - Об'єкт одного:   v-in-window="{ start:'25.10', end:'01.11' }"
 * - Кілька вікон:    v-in-window="{ any:[ ['25.10','01.11'], {start:'01.12',end:'10.01'} ], invert:true }"
 * @param {*} value - Значення директиви
 * @returns {{ windows: InWindowSingle[], options: InWindowOptions }} Нормалізовані вікна та опції
 */
function normalize(value) {
	/* @type {InWindowSingle[]} */
	const windows = [];
	/* @type {InWindowOptions} */
	const options = {};

	if (Array.isArray(value)) {
		if (value.length >= 2) {
			windows.push({ start: value[0], end: value[1] });
		}
	} else if (typeof value === "string") {
		const m = value.match(/(\S+)\s*[-–…]\s*(\S+)/); // "25.10 - 01.11"
		if (m) windows.push({ start: m[1], end: m[2] });
	} else if (value && typeof value === "object") {
		const start = value.start;
		const end = value.end;
		const any = value.any;
		const tz = value.tz;
		const invert = value.invert;
		const mode = value.mode;
		const onClass = value.onClass;
		const offClass = value.offClass;
		const refreshMs = value.refreshMs;

		if (any && Array.isArray(any)) {
			any.forEach(function(w) {
				if (Array.isArray(w) && w.length >= 2) {
					windows.push({ start: w[0], end: w[1] });
				} else if (w && typeof w === "object" && ("start" in w) && ("end" in w)) {
					windows.push({ start: w.start, end: w.end });
				} else if (w instanceof Date) {
					windows.push({ start: w, end: w });
				} else if (typeof w === "string") {
					const mm = w.match(/(\S+)\s*[-–…]\s*(\S+)/);
					if (mm) windows.push({ start: mm[1], end: mm[2] });
				}
			});
		} else if (start && end) {
			windows.push({ start: start, end: end });
		}

		if (tz) options.tz = tz;
		if (typeof invert === "boolean") options.invert = invert;
		if (mode) options.mode = mode;
		if (onClass) options.onClass = onClass;
		if (offClass) options.offClass = offClass;
		if (refreshMs != null && !Number.isNaN(Number(refreshMs))) {
			options.refreshMs = Number(refreshMs);
		}
	}

	return { windows: windows, options: options };
}

/*
 * Перевіряє, чи now потрапляє у будь-яке з вікон.
 * @param {InWindowSingle[]} windows - Список вікон
 * @param {Date} now - Поточний час
 * @returns {boolean} true, якщо активне хоча б одне вікно
 */
function isActiveAny(windows, now) {
	for (let i = 0; i < windows.length; i += 1) {
		if (isNowInWindow(windows[i], now)) return true;
	}
	return false;
}

/*
 * Застосовує видимість до елемента згідно режиму.
 * @param {HTMLElement} el - Елемент
 * @param {boolean} show - Чи показувати
 * @param {InWindowOptions} options - Опції
 * @returns {void}
 */
function applyVisibility(el, show, options) {
	const mode = (options && options.mode) || "display";
	const onClass = options && options.onClass;
	const offClass = options && options.offClass;

	// класи
	if (onClass) el.classList.toggle(onClass, show);
	if (offClass) el.classList.toggle(offClass, !show);

	if (mode === "classOnly") return;

	if (mode === "visibility") {
		el.style.visibility = show ? "" : "hidden";
		return;
	}

	// mode === "display"
	if (!show) {
		if (el.style.display !== "none") {
			// збережемо поточне inline-значення
			el.__inWindowSavedDisplay = el.style.display || "";
		}
		el.style.display = "none";
	} else {
		el.style.display = el.__inWindowSavedDisplay || "";
	}
}

/*
 * Обчислює, коли робити наступну перевірку:
 * - якщо refreshMs задано — через refreshMs
 * - інакше — на північ наступного дня в TZ (+5 секунд)
 * @param {InWindowOptions} options - Опції
 * @returns {number} Мілісекунди до наступної перевірки
 */
function msUntilNextCheck(options) {
	if (options && typeof options.refreshMs === "number" && options.refreshMs > 0) {
		return options.refreshMs;
	}
	const now = nowInTZ(options && options.tz);
	const next = new Date(now.getTime());
	next.setDate(now.getDate() + 1);
	next.setHours(0, 0, 5, 0); // 00:00:05
	return next.getTime() - now.getTime();
}

/*
 * Основна логіка оновлення видимості елемента.
 * @param {HTMLElement} el - Елемент
 * @param {*} value - Значення директиви
 * @returns {void}
 */
function updateEl(el, value) {
	const parsed = normalize(value);
	const windows = parsed.windows;
	const options = parsed.options;

	// Якщо не передали жодного вікна — не змінюємо видимість (показуємо)
	if (!windows.length) {
		applyVisibility(el, true, options);
		if (el.__inWindowTimer) {
			clearTimeout(el.__inWindowTimer);
			el.__inWindowTimer = null;
		}
		return;
	}

	const now = nowInTZ(options.tz);
	const active = isActiveAny(windows, now);
	const show = options.invert ? !active : active;
	applyVisibility(el, show, options);

	// Переналаштувати таймер
	if (el.__inWindowTimer) clearTimeout(el.__inWindowTimer);
	const timeout = msUntilNextCheck(options);
	el.__inWindowTimer = setTimeout(function() {
		updateEl(el, value); 
	}, timeout);
}

/* @type {{ bind: Function, update: Function, unbind: Function }} */
const InWindowDirective = {
	/*
	 * @param {HTMLElement} el - Елемент
	 * @param {{ value: * }} binding - Байндінг директиви
	 * @returns {void}
	 */
	bind: function bind(el, binding) {
		updateEl(el, binding.value);
	},
	/*
	 * @param {HTMLElement} el - Елемент
	 * @param {{ value: * }} binding - Нове значення
	 * @returns {void}
	 */
	update: function update(el, binding) {
		updateEl(el, binding.value);
	},
	/*
	 * @param {HTMLElement} el - Елемент
	 * @returns {void}
	 */
	unbind: function unbind(el) {
		if (el.__inWindowTimer) {
			clearTimeout(el.__inWindowTimer);
			el.__inWindowTimer = null;
		}
	}
};

export default {
	/*
	 * Плагін Vue: реєструє директиву v-in-window і додає прототипні хелпери.
	 * @param {import('vue').VueConstructor} Vue - Конструктор Vue
	 * @returns {void}
	 */
	install: function install(Vue) {
		Vue.directive("in-window", InWindowDirective);

		/*
		 * Перевірка одного вікна.
		 * @param {string|Date|[string,string]|InWindowSingle} start - Початок або опис вікна
		 * @param {string|Date=} end - Кінець (якщо передали окремо)
		 * @param {InWindowOptions=} opts - Опції
		 * @returns {boolean} true, якщо поточний момент в межах вікна
		 */
		Vue.prototype.$inWindow = function $inWindow(start, end, opts) {
			/* @type {InWindowSingle} */
			let w;
			if (start && typeof start === "object" && "start" in start) {
				w = /* @type {InWindowSingle} */ (start);
			} else if (Array.isArray(start)) {
				w = { start: start[0], end: start[1] };
			} else if (start instanceof Date && !end) {
				w = { start: start, end: start };
			} else {
				w = { start: start, end: end };
			}
			const now = nowInTZ((opts && opts.tz) || "Europe/Kyiv");
			return isNowInWindow(w, now);
		};

		/*
		 * Перетворює «будь-який формат» у {start, end}.
		 * @param {InWindowSingle|[string,string]|string|Date} input - Опис вікна
		 * @returns {InWindowSingle|null} Нормалізоване вікно або null
		 */
		function toWindow(input) {
			if (input && typeof input === "object" && "start" in input && "end" in input) {
				return /* @type {InWindowSingle} */ (input);
			}
			if (Array.isArray(input) && input.length >= 2) {
				return { start: input[0], end: input[1] };
			}
			if (input instanceof Date) {
				return { start: input, end: input };
			}
			if (typeof input === "string") {
				const m = input.match(/(\S+)\s*[-–…]\s*(\S+)/);
				if (m) return { start: m[1], end: m[2] };
			}
			return null;
			// Примітка: окремий рядок без «-» (наприклад '25.10') тут спеціально не підтримуємо,
			// щоб уникнути неочікуваної поведінки. Для одноденного вікна передавай Date або [d, d].
		}

		/*
		 * true, якщо «хоч одне» вікно активне.
		 * @param {Array<InWindowSingle|[string,string]|string|Date>} windows - Набір вікон
		 * @param {{tz?: string}=} opts - Опції
		 * @returns {boolean} Результат перевірки
		 */
		Vue.prototype.$inWindowAny = function $inWindowAny(windows, opts) {
			const list = Array.isArray(windows) ? windows : [ windows ];
			const now = nowInTZ((opts && opts.tz) || "Europe/Kyiv");
			return list.some(function(item) {
				const w = toWindow(item);
				return w ? isNowInWindow(w, now) : false;
			});
		};

		/*
		 * true, якщо «жодне» з вікон не активне (зручно для дефолтних блоків).
		 * @param {Array<InWindowSingle|[string,string]|string|Date>} windows - Набір вікон
		 * @param {{tz?: string}=} opts - Опції
		 * @returns {boolean} Результат перевірки
		 */
		Vue.prototype.$inWindowNone = function $inWindowNone(windows, opts) {
			return !this.$inWindowAny(windows, opts);
		};
	}
};
